import { useMutation, useQueryClient } from '@tanstack/react-query';

import createTaxPayment from 'modules/Tax/api/createTaxPayment';
import invalidateCalculatorsForPayment from 'modules/Tax/queries/invalidateCalculatorsForPayment';
import { MEMBER_TASKS_QUERY_KEY } from 'services/memberTasks/queries/useMemberTasks';

const useCreateTaxPaymentMutation = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (payload: any) => {
            return createTaxPayment(payload);
        },
        /*
         * Cache housekeeping, never a verdict on the write.
         *
         * React Query awaits this before the caller's own `onSuccess`, and routes a rejection here
         * to the caller's `onError` instead. So one failing refetch — and `refetchQueries` on this
         * broad key re-requests every cached variant, including ones for surfaces nobody is looking
         * at — turned a payment the backend had already accepted into "Failed to record payment",
         * with the modal left open on a form that had in fact saved. Swallow it: the POST is what
         * says whether the payment landed, and a stale list refreshes on the next read.
         */
        onSuccess: async (_data, payload) => {
            await queryClient
                .refetchQueries({
                    queryKey: ['listTaxPayments'],
                })
                .catch(() => {});
            /*
             * A payment created with a receipt is posted as multipart, where the fields
             * are entries rather than properties — reading `.payment_type` off a FormData
             * yields undefined, which would widen the invalidation to both calculators.
             */
            const paymentType =
                payload instanceof FormData ? payload.get('payment_type')?.toString() : payload?.payment_type;
            await Promise.all([
                invalidateCalculatorsForPayment(queryClient, paymentType),
                /*
                 * The backend settles the payment's task card in this same request, so the member
                 * task list this browser is holding is out of date the moment the POST returns.
                 * Without this the surfaces that read it — the Quarterly payments tab markers, the
                 * task feed — keep the card open until a reload.
                 */
                queryClient.invalidateQueries({ queryKey: MEMBER_TASKS_QUERY_KEY }),
            ]).catch(() => {});
        },
    });
    return mutation;
};

export default useCreateTaxPaymentMutation;
